import { useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import { useErrors } from '../../hooks/useErrors.js';
import BaseButton from '../../components/form/BaseButton.jsx';
import BaseUpload from '../../components/form/BaseUpload.jsx';
import { useGetPresignedUrlQuery, useImportFilesMutation } from '../../api/file.js';
import { FILE_IMPORT_SCHEMA, getFileExtension, getFileNameWithoutExtension } from '../../utils/file.js';
import { useSelector } from 'react-redux';
import SharedModelListItem from '../../components/misc/SharedModelListItem.jsx';
import PrivateUsersListItem from '../../components/misc/PrivateUsersListItem.jsx';
import { PERMISSION_FILES } from '../../utils/constants.js';
import axios from 'axios';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import BaseDropdown from '../../components/form/BaseDropdown.jsx';
import { useGetCountriesQuery } from '../../api/misc.js';

const ImportFiles = () => {
    const [importFiles, { isLoading, error }] = useImportFilesMutation();
    const [isPrivate, setIsPrivate] = useState(true);
    const [presignedUrl, setPresignedUrl] = useState('');
    const [fields, setFields] = useState([]);
    const user = useSelector((state) => state.user.user);
    const [permittedOrganizations, setPermittedOrganizations] = useState([]);
    const [s3Error, setS3Error] = useState('');
    const navigate = useNavigate();

    const { permissions: userPermissions } = useCurrentUser();

    useEffect(() => {
        if (!user?.role?.permissions || !user?.organizations) return;

        user.organizations.forEach((item) => {
            const permission = user.role.permissions.find(
                (value) => value.organization_id === item.id && value.permission === PERMISSION_FILES
            );

            const hasPermission = permission && permission.create;

            if (hasPermission) {
                setPermittedOrganizations((prevState) => {
                    if (!prevState.some((org) => org.id === item.id)) {
                        return [...prevState, item];
                    }
                    return prevState;
                });
            }
        });
    }, [user]);

    const { data, isLoadingData, errorData } = userPermissions[PERMISSION_FILES].create
        ? useGetPresignedUrlQuery()
        : { data: [], isLoading: false };

    const { data: countriesData } = useGetCountriesQuery({});
    const countries = useMemo(() => {
        if (countriesData?.response?.countries) {
            return countriesData.response.countries.map((country) => ({ label: country.name, value: country.id }));
        }

        return [];
    }, [countriesData]);

    const extensions = {
        bmp: 'media',
        gif: 'media',
        png: 'media',
        jpg: 'media',
        jpeg: 'media',
        webp: 'media',
        tif: 'media',
        tiff: 'media',
        heic: 'media',
        heif: 'media',
        svg: 'media',
        mp3: 'media',
        wav: 'media',
        mp4: 'media',
        avi: 'media',
        pdf: 'document',
        doc: 'document',
        docx: 'document',
        txt: 'document',
        xls: 'spreadsheet',
        xlsx: 'spreadsheet',
        csv: 'spreadsheet'
    };
    const { handleSubmit, getValues, formState, control, clearErrors, reset, setValue, watch } = useForm({
        resolver: yupResolver(FILE_IMPORT_SCHEMA),
        defaultValues: {
            files: [],
            shared: false,
            shared_organizations: [],
            shared_users: [],
            country_id: ''
        },
        reValidateMode: 'onSubmit'
    });

    const { errors } = useErrors(error?.message, formState);
    const currentFiles = watch('files');

    const generateUniqueFileName = (originalName) => {
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substring(2, 8);
        const extension = originalName.split('.').pop();
        return `${timestamp}_${randomString}.${extension}`;
    };

    const uploadFilesToS3 = async (file) => {
        try {
            const uniqueFilename = generateUniqueFileName(file.name);
            const fileKey = `${fields.key}${uniqueFilename}`;

            const formData = new FormData();
            const updatedFields = { ...fields, key: fileKey };

            Object.entries(updatedFields).forEach(([key, value]) => {
                formData.append(key, value);
            });

            formData.append('file', file);

            const response = await axios.post(presignedUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.status !== 204) {
                throw new Error(`Failed to upload file: ${response.statusText}`);
            }

            const extension = getFileExtension(file.name);

            return {
                name: getFileNameWithoutExtension(file.name),
                path: fileKey,
                extension,
                type: extensions[extension]
            };
        } catch (error) {
            if (error.response) {
                if (error.response.status === 403 || error.response.status === 401) {
                    setS3Error('Invalid credentials provided for S3 upload.');
                } else {
                    setS3Error(`S3 upload failed: ${error.response.statusText}`);
                }
            } else if (error.request) {
                setS3Error('No response received from S3. Please check your network.');
            } else {
                setS3Error('Unexpected error during file upload.');
            }
        }
    };

    const handleFileUpload = async (requestData) => {
        const { files } = requestData;
        if (files.length > 0 && presignedUrl !== '' && Object.keys(fields).length > 0 && !isLoadingData) {
            try {
                const uploadedFilePaths = await Promise.all(
                    files.map(async (file) => {
                        return await uploadFilesToS3(file);
                    })
                );

                if (s3Error === '') {
                    await sendImportFile(requestData, uploadedFilePaths);
                }
            } catch (error) {
                console.error('Error during file upload:', error);
            }
        }
    };

    const sendImportFile = async (requestData, filePaths) => {
        const fileData = {
            ...(requestData.shared_users.length > 0 ? { shared_users: sharedUsers } : {}),
            ...(requestData.shared_organizations.length > 0 ? { shared_organizations: sharedOrganizations } : {}),
            shared: !!requestData.shared_organizations.length,
            country_id: requestData.country_id,
            files: filePaths
        };

        await importFiles(fileData).then((apiResponse) => {
            if (apiResponse.data?.success) {
                navigate('/files');
            }
        });
    };

    const onFileUpload = (acceptedFile) => {
        setValue('files', acceptedFile, { shouldValidate: true });
        setS3Error('');
    };

    const onRemoveNewUploadedFile = (index) => {
        setValue(
            'files',
            currentFiles.filter((_, i) => i !== index)
        );
        setS3Error('');
    };

    const sharedOrganizations = watch('shared_organizations');
    const sharedUsers = watch('shared_users');

    const handleSharedOrganizations = (index, value) => {
        if (permittedOrganizations.length > 0) {
            const organizationId = permittedOrganizations[index].id;

            const updatedSharedOrganizations = value
                ? [...sharedOrganizations, organizationId]
                : sharedOrganizations.filter((orgId) => orgId !== organizationId);

            setValue('shared_organizations', updatedSharedOrganizations);

            if (updatedSharedOrganizations.length === 0) {
                handleSharedUsers(true);
            }
        }
    };

    const handleSharedUsers = (value) => {
        setIsPrivate(value);
        if (value) {
            setValue('shared_organizations', []);
        } else {
            setValue('shared_users', []);
            if (permittedOrganizations.length > 0) {
                handleSharedOrganizations(0, true);
            }
        }
    };

    const handleUserMark = (id, value) => {
        const updatedSharedUsers = value ? [...sharedUsers, id] : sharedUsers.filter((id) => id !== id);

        setValue('shared_users', updatedSharedUsers);
    };

    const renderShared = () => {
        if (permittedOrganizations.length === 0) {
            return <div className='text-theme1-text1 text-lg'>No organizations found.</div>;
        }

        return (
            <div>
                {permittedOrganizations.map(({ id, name }, index) => (
                    <SharedModelListItem
                        key={id}
                        index={index}
                        text={name}
                        isMarked={sharedOrganizations.includes(id)}
                        onChangeSwitch={handleSharedOrganizations}
                        disabled={isPrivate}
                    />
                ))}

                <PrivateUsersListItem
                    key='separate-item'
                    text={'Private'}
                    isMarked={isPrivate}
                    onChangeSwitch={handleSharedUsers}
                    onChangeMark={handleUserMark}
                    modelSharedUsers={[]}
                    permission={PERMISSION_FILES}
                />
            </div>
        );
    };

    const formValues = getValues();

    useEffect(() => {
        if (data && data?.response) {
            setPresignedUrl(data.response.url);
            setFields(data.response.fields);
        }
    }, [data]);

    return (
        <AuthLayout mainClassNames={'bg-theme1-white'} title={'Import Files'}>
            <div className='bg-theme1-pinkGray max-w-3xl w-full mx-auto p-8'>
                <div>
                    <div className='text-lg font-bold text-theme1-text2 mb-2'>
                        Shared within <span className='text-theme1-brightRed'>*</span>
                    </div>
                    {renderShared()}
                </div>
            </div>
            <div className='max-w-3xl w-full mx-auto px-8'>
                <div className='mt-8 text-theme1-title2 font-bold text-xl'>Import files</div>
                <div className='mb-10'>
                    <BaseDropdown
                        className='mt-4'
                        control={control}
                        label='Country'
                        name='country_id'
                        clearErrors={clearErrors}
                        errors={[]}
                        options={countries}
                    />
                </div>
                <div className='mb-10'>
                    <BaseUpload
                        accept={Object.keys(extensions)}
                        newUploadedFiles={currentFiles}
                        onFilesUpload={onFileUpload}
                        multiple={true}
                        onRemoveNewUploadedFile={onRemoveNewUploadedFile}
                        reset={reset}
                        canDelete={false}
                        errors={errors.file || [s3Error]}
                        isLoading={isLoading}
                    />
                </div>
            </div>
            <div className='max-w-3xl w-full mx-auto px-8'>
                <div className='mb-6'>
                    <div className='grid grid-cols-2 gap-4'>
                        <BaseButton
                            id='btn-submit'
                            className='btn-primary-outline'
                            onClick={handleSubmit(handleFileUpload)}
                            isLoading={isLoading || isLoadingData}
                            textValue={isLoading ? 'Importing...' : 'Import'}
                            disabled={!formValues.files || isLoading || isLoadingData}
                        />

                        <BaseButton
                            id='btn-cancel'
                            className='btn-primary-outline'
                            onClick={() => navigate('/files')}
                            textValue='Cancel'
                        />
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
};

export default ImportFiles;
